export type ShareLocale = "en" | "pt";
export type SharePlacement = "preview" | "purchase";
export type ShareChannel = "native" | "copy" | "x" | "linkedin" | "whatsapp";

export const SHARE_COOKIE = "__Host-refound_ref";
export const SHARE_COOKIE_TTL_SECONDS = 30 * 24 * 60 * 60;

export interface ShareOpportunitySummary {
  slug: string;
  name: string;
  hasReviewedPrompt: boolean;
  batch?: string | null;
  reportedStatus?: string | null;
  oneLiner?: string | null;
  location?: string | null;
}

export interface ShareLocalizedCopy {
  title: string;
  text: string;
  regionLabel: string;
  prompt: string;
  shareOnX: string;
  shareOnLinkedIn: string;
  shareOnWhatsApp: string;
  copyLink: string;
  openingX: string;
  openingShare: string;
  copying: string;
  copied: string;
  xOpened: string;
  shareOpened: string;
  error: string;
  privacy: string;
}

export interface ShareIntentUrls {
  x: string;
  linkedin: string;
  whatsapp: string;
}

export function validShareLocale(value: unknown): value is ShareLocale {
  return value === "en" || value === "pt";
}

export function validSharePlacement(value: unknown): value is SharePlacement {
  return value === "preview" || value === "purchase";
}

export function validShareChannel(value: unknown): value is ShareChannel {
  return (
    value === "native" ||
    value === "copy" ||
    value === "x" ||
    value === "linkedin" ||
    value === "whatsapp"
  );
}

export function validShareCode(value: unknown): value is string {
  return typeof value === "string" && /^[A-Za-z0-9_-]{20,48}$/.test(value);
}

function cleanCompanyName(value: string): string {
  return Array.from(value, (character) => {
    const codePoint = character.codePointAt(0) ?? 0;
    return codePoint < 32 || codePoint === 127 ? " " : character;
  })
    .join("")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 90) || "This company";
}

export function localizedShareCopy(
  locale: ShareLocale,
  companyName: string,
  placement: SharePlacement,
): ShareLocalizedCopy {
  const name = cleanCompanyName(companyName);
  if (locale === "pt") {
    const text =
      placement === "purchase"
        ? `Eu destravei a tese de reconstrução de ${name}. O preço sobe R$ 20 no Brasil ou US$ 4 nos demais mercados a cada novo comprador — veja o brief público e decida se esta ideia merece voltar.`
        : `O que poderia ser reconstruído a partir de ${name} hoje? Explore o brief público e o menor teste de MVP.`;
    return {
      title: `${name}: o que poderia funcionar hoje?`,
      text,
      regionLabel: `Compartilhar oportunidade: ${name}`,
      prompt:
        placement === "purchase"
          ? `Desafie alguém a reconstruir ${name}.`
          : `Quem deveria reconstruir ${name}?`,
      shareOnX: "Compartilhar no X",
      shareOnLinkedIn: "LinkedIn",
      shareOnWhatsApp: "WhatsApp",
      copyLink: "Copiar link",
      openingX: "Abrindo o compositor do X…",
      openingShare: "Preparando compartilhamento…",
      copying: "Preparando link…",
      copied: "Link copiado.",
      xOpened: "Compositor do X aberto.",
      shareOpened: "Compartilhamento aberto.",
      error: "Não foi possível preparar o link agora.",
      privacy:
        placement === "purchase"
          ? "Sua compra, identidade e prompt continuam privados."
          : "O link compartilha apenas o brief público; nenhum contato é acessado.",
    };
  }
  const text =
    placement === "purchase"
      ? `I unlocked the rebuild thesis for ${name}. Each new buyer raises the price by R$20 in Brazil or US$4 elsewhere — see the public brief and decide whether this idea deserves another life.`
      : `What could be rebuilt from ${name} today? Explore the public brief and the smallest MVP test.`;
  return {
    title: `${name}: what could work now?`,
    text,
    regionLabel: `Share opportunity: ${name}`,
    prompt:
      placement === "purchase"
        ? `Challenge someone to rebuild ${name}.`
        : `Who should rebuild ${name}?`,
    shareOnX: "Share on X",
    shareOnLinkedIn: "LinkedIn",
    shareOnWhatsApp: "WhatsApp",
    copyLink: "Copy link",
    openingX: "Opening the X composer…",
    openingShare: "Preparing share…",
    copying: "Preparing link…",
    copied: "Link copied.",
    xOpened: "X composer opened.",
    shareOpened: "Share opened.",
    error: "The share link could not be prepared right now.",
    privacy:
      placement === "purchase"
        ? "Your purchase, identity, and prompt stay private."
        : "The link shares only the public brief and never accesses your contacts.",
  };
}

function validatedOrigin(origin: string): URL {
  const value = new URL(origin);
  if (value.protocol !== "https:" && value.hostname !== "localhost" && value.hostname !== "127.0.0.1") {
    throw new Error("invalid_share_origin");
  }
  value.pathname = "/";
  value.search = "";
  value.hash = "";
  return value;
}

function validatedSlug(slug: string): string {
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) throw new Error("invalid_share_slug");
  return slug;
}

export function canonicalOpportunityUrl(
  origin: string,
  locale: ShareLocale,
  slug: string,
): URL {
  const url = validatedOrigin(origin);
  url.pathname = `/${locale}/company/${validatedSlug(slug)}`;
  return url;
}

export function trackedOpportunityUrl(
  origin: string,
  locale: ShareLocale,
  slug: string,
  placement: SharePlacement,
  channel: ShareChannel,
  shareCode: string,
): URL {
  if (!validShareCode(shareCode)) throw new Error("invalid_share_code");
  const url = canonicalOpportunityUrl(origin, locale, slug);
  url.searchParams.set("utm_source", "refound_share");
  url.searchParams.set("utm_medium", "referral");
  url.searchParams.set("utm_campaign", "opportunity");
  url.searchParams.set("utm_content", `${placement}_${channel}`);
  url.searchParams.set("ref", shareCode);
  return url;
}

export function shareIntentUrls(shareUrl: string, text: string): ShareIntentUrls {
  const x = new URL("https://twitter.com/intent/tweet");
  x.searchParams.set("text", text);
  x.searchParams.set("url", shareUrl);

  const linkedin = new URL("https://www.linkedin.com/sharing/share-offsite/");
  linkedin.searchParams.set("url", shareUrl);

  const whatsapp = new URL("https://wa.me/");
  whatsapp.searchParams.set("text", `${text}\n${shareUrl}`);
  return { x: x.toString(), linkedin: linkedin.toString(), whatsapp: whatsapp.toString() };
}

export function validatedXIntentUrl(value: unknown, expectedShareUrl: string): string {
  if (typeof value !== "string") throw new Error("invalid_x_intent");
  let intent: URL;
  try {
    intent = new URL(value);
  } catch {
    throw new Error("invalid_x_intent");
  }
  if (
    intent.protocol !== "https:" ||
    intent.hostname !== "twitter.com" ||
    intent.port !== "" ||
    intent.username !== "" ||
    intent.password !== "" ||
    intent.pathname !== "/intent/tweet" ||
    intent.hash !== "" ||
    intent.searchParams.get("url") !== expectedShareUrl
  ) {
    throw new Error("invalid_x_intent");
  }
  return intent.toString();
}

export function validatedShareIntentUrl(
  channel: "x" | "linkedin" | "whatsapp",
  value: unknown,
  expectedShareUrl: string,
): string {
  if (channel === "x") return validatedXIntentUrl(value, expectedShareUrl);
  if (typeof value !== "string") throw new Error("invalid_share_intent");
  let intent: URL;
  try {
    intent = new URL(value);
  } catch {
    throw new Error("invalid_share_intent");
  }
  const safeBase =
    intent.protocol === "https:" &&
    intent.port === "" &&
    intent.username === "" &&
    intent.password === "" &&
    intent.hash === "";
  const validLinkedIn =
    channel === "linkedin" &&
    intent.hostname === "www.linkedin.com" &&
    intent.pathname === "/sharing/share-offsite/" &&
    intent.searchParams.get("url") === expectedShareUrl;
  const validWhatsApp =
    channel === "whatsapp" &&
    intent.hostname === "wa.me" &&
    intent.pathname === "/" &&
    (intent.searchParams.get("text") ?? "").includes(expectedShareUrl);
  if (!safeBase || (!validLinkedIn && !validWhatsApp)) {
    throw new Error("invalid_share_intent");
  }
  return intent.toString();
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export interface SharePageMetadata {
  locale: ShareLocale;
  title: string;
  description: string;
  canonicalUrl: string;
  alternateEnUrl: string;
  alternatePtUrl: string;
  imageUrl: string;
}

export const PUBLIC_OPPORTUNITY_COUNT = 959;

export function localizedPublicOpportunityCount(locale: ShareLocale): string {
  return new Intl.NumberFormat(locale === "pt" ? "pt-BR" : "en-US").format(
    PUBLIC_OPPORTUNITY_COUNT,
  );
}

function canonicalHomeUrl(origin: string, locale: ShareLocale): URL {
  const url = validatedOrigin(origin);
  url.pathname = `/${locale}/`;
  return url;
}

export function homeSharePageMetadata(
  origin: string,
  locale: ShareLocale,
): SharePageMetadata {
  const canonicalUrl = canonicalHomeUrl(origin, locale).toString();
  const alternateEnUrl = canonicalHomeUrl(origin, "en").toString();
  const alternatePtUrl = canonicalHomeUrl(origin, "pt").toString();
  const imageUrl = new URL(`/share/${locale}/home/card.png`, validatedOrigin(origin)).toString();
  if (locale === "pt") {
    return {
      locale,
      title: "Startups morrem. Ideias não. | Re:Found",
      description: `${localizedPublicOpportunityCount(locale)} dossiês de oportunidades de startups encerradas da Y Combinator. Encontre o que vale construir de novo.`,
      canonicalUrl,
      alternateEnUrl,
      alternatePtUrl,
      imageUrl,
    };
  }
  return {
    locale,
    title: "Startups die. Ideas don't. | Re:Found",
    description: `${localizedPublicOpportunityCount(locale)} opportunity dossiers from dead Y Combinator startups. Find what's worth rebuilding.`,
    canonicalUrl,
    alternateEnUrl,
    alternatePtUrl,
    imageUrl,
  };
}

export function sharePageMetadata(
  origin: string,
  locale: ShareLocale,
  opportunity: ShareOpportunitySummary,
): SharePageMetadata {
  const name = cleanCompanyName(opportunity.name);
  const canonicalUrl = canonicalOpportunityUrl(origin, locale, opportunity.slug).toString();
  const alternateEnUrl = canonicalOpportunityUrl(origin, "en", opportunity.slug).toString();
  const alternatePtUrl = canonicalOpportunityUrl(origin, "pt", opportunity.slug).toString();
  const imageUrl = new URL(
    `/share/${locale}/company/${validatedSlug(opportunity.slug)}/card.png`,
    validatedOrigin(origin),
  ).toString();
  if (locale === "pt") {
    return {
      locale,
      title: `${name}: o que poderia funcionar hoje? | Re:Found`,
      description:
        "Histórico público da empresa e um caminho independente para investigar o que ainda vale testar.",
      canonicalUrl,
      alternateEnUrl,
      alternatePtUrl,
      imageUrl,
    };
  }
  return {
    locale,
    title: `${name}: what could work now? | Re:Found`,
    description:
      "Public company history and an independent path for investigating what may still be worth testing.",
    canonicalUrl,
    alternateEnUrl,
    alternatePtUrl,
    imageUrl,
  };
}

export function injectShareMetadata(html: string, metadata: SharePageMetadata): string {
  const title = escapeHtml(metadata.title);
  const description = escapeHtml(metadata.description);
  const canonicalUrl = escapeHtml(metadata.canonicalUrl);
  const imageUrl = escapeHtml(metadata.imageUrl);
  const alternateEnUrl = escapeHtml(metadata.alternateEnUrl);
  const alternatePtUrl = escapeHtml(metadata.alternatePtUrl);
  const ogLocale = metadata.locale === "pt" ? "pt_BR" : "en_US";
  const alternateLocale = metadata.locale === "pt" ? "en_US" : "pt_BR";
  const tags = [
    `<title>${title}</title>`,
    `<meta name="description" content="${description}" />`,
    `<link rel="canonical" href="${canonicalUrl}" />`,
    `<link rel="alternate" hreflang="en" href="${alternateEnUrl}" />`,
    `<link rel="alternate" hreflang="pt-BR" href="${alternatePtUrl}" />`,
    `<link rel="alternate" hreflang="x-default" href="${alternateEnUrl}" />`,
    `<meta property="og:type" content="website" />`,
    `<meta property="og:site_name" content="Re:Found" />`,
    `<meta property="og:locale" content="${ogLocale}" />`,
    `<meta property="og:locale:alternate" content="${alternateLocale}" />`,
    `<meta property="og:title" content="${title}" />`,
    `<meta property="og:description" content="${description}" />`,
    `<meta property="og:url" content="${canonicalUrl}" />`,
    `<meta property="og:image" content="${imageUrl}" />`,
    `<meta property="og:image:type" content="image/png" />`,
    `<meta property="og:image:width" content="1200" />`,
    `<meta property="og:image:height" content="630" />`,
    `<meta property="og:image:alt" content="${title}" />`,
    `<meta name="twitter:card" content="summary_large_image" />`,
    `<meta name="twitter:title" content="${title}" />`,
    `<meta name="twitter:description" content="${description}" />`,
    `<meta name="twitter:image" content="${imageUrl}" />`,
    `<meta name="twitter:image:alt" content="${title}" />`,
  ].join("\n    ");

  const cleaned = html
    .replace(/<title\b[^>]*>[\s\S]*?<\/title>\s*/gi, "")
    .replace(/<meta\b[^>]*\bname=["']description["'][^>]*>\s*/gi, "")
    .replace(/<meta\b[^>]*\b(?:property|name)=["'](?:og:[^"']+|twitter:[^"']+)["'][^>]*>\s*/gi, "")
    .replace(/<link\b[^>]*\brel=["'](?:canonical|alternate)["'][^>]*>\s*/gi, "");
  const language = metadata.locale === "pt" ? "pt-BR" : "en";
  const localized = /<html\b[^>]*\blang=["'][^"']*["']/i.test(cleaned)
    ? cleaned.replace(/(<html\b[^>]*\blang=)["'][^"']*["']/i, `$1"${language}"`)
    : cleaned.replace(/<html\b/i, `<html lang="${language}"`);
  if (!/<\/head>/i.test(localized)) throw new Error("share_html_head_missing");
  return localized.replace(/<\/head>/i, `    ${tags}\n  </head>`);
}

export function injectPublicCompanyBrief(
  html: string,
  locale: ShareLocale,
  opportunity: ShareOpportunitySummary,
): string {
  const rootPattern = /<div\s+id=["']root["'][^>]*>\s*<\/div>/i;
  if (!rootPattern.test(html)) throw new Error("share_html_root_missing");
  const name = escapeHtml(cleanCompanyName(opportunity.name));
  const oneLiner = escapeHtml(
    opportunity.oneLiner?.trim() ||
      (locale === "pt"
        ? "Registro verificado de uma empresa no arquivo público da Re:Found."
        : "A verified company record in Re:Found's public archive."),
  );
  const batch = opportunity.batch?.trim() ? escapeHtml(opportunity.batch.trim()) : null;
  const status = opportunity.reportedStatus?.trim()
    ? escapeHtml(opportunity.reportedStatus.trim())
    : null;
  const location = opportunity.location?.trim()
    ? escapeHtml(opportunity.location.trim())
    : null;
  const facts = [
    batch ? `<div><dt>${locale === "pt" ? "Turma YC" : "YC batch"}</dt><dd>${batch}</dd></div>` : "",
    status ? `<div><dt>${locale === "pt" ? "Status reportado" : "Reported status"}</dt><dd>${status}</dd></div>` : "",
    location ? `<div><dt>${locale === "pt" ? "Localização reportada" : "Reported location"}</dt><dd>${location}</dd></div>` : "",
  ].filter(Boolean).join("");
  const boundary = opportunity.hasReviewedPrompt
    ? locale === "pt"
      ? "Leia o briefing público gratuitamente. A tese, as evidências, o playbook de validação e o prompt de MVP exigem acesso autorizado."
      : "Read the public briefing for free. The thesis, evidence, validation playbook, and MVP prompt require authorized access."
    : locale === "pt"
      ? "Esta empresa está em curadoria. Nenhuma tese, playbook ou prompt está à venda."
      : "This company is under editorial review. No thesis, playbook, or prompt is for sale.";
  const sourceLabel = locale === "pt" ? "Abrir fonte pública" : "Open public source";
  const exploreLabel = locale === "pt" ? "Explorar o arquivo" : "Explore the archive";
  const fallback = `<main id="refound-public-brief" lang="${locale === "pt" ? "pt-BR" : "en"}">
      <p>${locale === "pt" ? "BRIEFING PÚBLICO DA EMPRESA" : "PUBLIC COMPANY BRIEF"}</p>
      <h1>${name}</h1>
      <p>${oneLiner}</p>
      ${facts ? `<dl>${facts}</dl>` : ""}
      <p>${escapeHtml(boundary)}</p>
      <nav aria-label="${locale === "pt" ? "Ações do briefing" : "Brief actions"}">
        <a href="/${locale}/">${exploreLabel}</a>
        <a href="https://startups.rip/company/${validatedSlug(opportunity.slug)}" rel="external noreferrer">${sourceLabel}</a>
      </nav>
    </main>`;
  return html.replace(rootPattern, `<div id="root">${fallback}</div>`);
}

export function shareReferralCookie(code: string): string {
  if (!validShareCode(code)) throw new Error("invalid_share_code");
  return `${SHARE_COOKIE}=${encodeURIComponent(code)}; Path=/; Max-Age=${SHARE_COOKIE_TTL_SECONDS}; HttpOnly; Secure; SameSite=Lax; Priority=Medium`;
}
